import { useState, useEffect, useRef, useCallback } from 'react';
import { checkBackendHealth, setBackendReachable } from '../api/worker';
import { SHOULD_USE_MOCKS, ENABLE_HEALTH_CHECKS } from '../lib/clerk';

export function useBackendHealth(pollingInterval = 60000, requiredFailures = 2) {
  const [isMaintenanceMode, setIsMaintenanceMode] = useState(false);
  const [isChecking, setIsChecking] = useState(false);
  const [lastChecked, setLastChecked] = useState<Date | null>(null);
  const [consecutiveFailures, setConsecutiveFailures] = useState(0);
  const isMounted = useRef(true);

  const monitorHealth = useCallback(async () => {
    if (SHOULD_USE_MOCKS || !ENABLE_HEALTH_CHECKS || !isMounted.current) {
      if (isMounted.current) {
        setIsMaintenanceMode(false);
        setConsecutiveFailures(0);
      }
      return true;
    }

    setIsChecking(true);
    try {
      // Direct return if health checks are intentionally disabled at the environment level
      if (ENABLE_HEALTH_CHECKS !== true) {
        setIsChecking(false);
        setConsecutiveFailures(0);
        setIsMaintenanceMode(false);
        return true;
      }
      
      const isHealthy = await checkBackendHealth();
      
      // Update global worker state so individual requests can fail fast
      setBackendReachable(isHealthy);
      
      if (!isMounted.current) return isHealthy;
      
      setIsChecking(false);
      setLastChecked(new Date());

      if (!isHealthy) {
        setConsecutiveFailures(prev => {
          const next = prev + 1;
          if (next >= requiredFailures) {
            setIsMaintenanceMode(true);
          }
          return next;
        });
      } else {
        setConsecutiveFailures(0);
        setIsMaintenanceMode(false);
      }
      return isHealthy;
    } catch (e) {
      if (isMounted.current) setIsChecking(false);
      return false;
    }
  }, [requiredFailures]);

  useEffect(() => {
    isMounted.current = true;

    // Listener for successful API responses elsewhere in the app
    const handleReachable = () => {
      if (isMounted.current) {
        setIsMaintenanceMode(false);
        setConsecutiveFailures(0);
      }
    };

    if (typeof window !== 'undefined') {
      window.addEventListener('backend:reachable', handleReachable);
    }

    let interval: NodeJS.Timeout | null = null;
    if (SHOULD_USE_MOCKS || !ENABLE_HEALTH_CHECKS) {
      setBackendReachable(true);
      // isMaintenanceMode defaults to false at mount; monitorHealth() keeps it in sync.
    } else {
      console.log('[Health] Starting background health polling.');
      // Defer the first check off the effect's synchronous path; the interval
      // callback and this microtask share the same monitorHealth behavior.
      queueMicrotask(() => monitorHealth());
      interval = setInterval(monitorHealth, pollingInterval);
    }
    
    return () => {
      isMounted.current = false;
      if (interval) clearInterval(interval);
      if (typeof window !== 'undefined') {
        window.removeEventListener('backend:reachable', handleReachable);
      }
    };
  }, [monitorHealth, pollingInterval]);

  return {
    isMaintenanceMode,
    isChecking,
    lastChecked,
    consecutiveFailures,
    refresh: monitorHealth
  };
}